import { keyframes } from '@mui/system';
import { IContest, JudgingStatus } from '../../../../types/global/helper';

const MONGO_OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

export const pulseDot = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

export const formatJudgingStatusLabel = (status: string | undefined) =>
    (status ?? 'submitted').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/** Jury / voting scores — 1 decimal place (avoids float artifacts like 4.699999999999999). */
export const formatJuryScore = (score: number | null | undefined): string => {
    if (score == null || Number.isNaN(Number(score))) return '—';
    return Number(score).toFixed(1);
};

/**
 * Entries remaining for jury review — never under-report when incomplete count and
 * (total − completed) disagree (e.g. showing 0 while one entry still needs review).
 */
export const computeJuryEntriesRemainingForReview = (
    totalCount: number,
    completedSubmissionCount: number,
    incompletedSubmissionCount: number,
): number => {
    const derived = Math.max(0, totalCount - completedSubmissionCount);
    return Math.max(incompletedSubmissionCount, derived);
};

/** Human-readable tier label — resolves Mongo tier IDs via contest category tier_fee. */
export const resolveAwardTierDisplayLabel = (
    award: { tier_name?: string; tier_label?: string },
    contest?: IContest,
): string | null => {
    const candidates = [award.tier_label, award.tier_name].filter(Boolean) as string[];

    for (const raw of candidates) {
        const trimmed = raw.trim();
        if (!trimmed) continue;
        if (!MONGO_OBJECT_ID_RE.test(trimmed)) {
            return trimmed.replace(/\s+winner$/i, '').trim();
        }
        for (const cat of contest?.categories ?? []) {
            const tier = cat.tier_fee?.find(t => t._id === trimmed);
            if (tier?.label?.trim()) return tier.label.trim();
        }
    }

    return null;
};

/** Green-forward status pill — matches jury-duty “active” styling; default is green not blue. */
export const judgingStatusVisual = (status: JudgingStatus | string | undefined) => {
    const s = (status ?? 'submitted').toLowerCase();
    if (s === 'winner' || s === 'selected')
        return {
            label: formatJudgingStatusLabel(s),
            bg: 'rgba(255, 198, 62, 0.12)',
            color: '#ffc63e',
            border: 'rgba(255, 198, 62, 0.35)',
            dot: '#ffc63e',
            pulse: true,
        };
    if (s === 'closed' || s === 'rejected')
        return {
            label: formatJudgingStatusLabel(s),
            bg: 'rgba(127, 29, 29, 0.2)',
            color: '#f87171',
            border: 'rgba(239, 68, 68, 0.2)',
            dot: '#ef4444',
            pulse: false,
        };
    return {
        label: formatJudgingStatusLabel(s),
        bg: 'rgba(20, 83, 45, 0.2)',
        color: '#4ade80',
        border: 'rgba(34, 197, 94, 0.2)',
        dot: '#22c55e',
        pulse: s === 'finalist' || s === 'semi-finalist',
    };
};
